/**
 * GoalDataEntryTray — Lead-only data entry surface for a Mini-Project
 * Mission goal (DEV-1075 Phase 7.3a-ii).
 *
 * Sibling to GoalEditTray. The two surfaces split per the DEV-841
 * principle locked on FP — pencil = "edit settings" (GoalEditTray),
 * database icon = "edit data" (this component). Same Tray chrome.
 *
 * Opens via the database icon on MiniProjectGoalCard (Lead-only).
 * Hidden for checklist primitives: the card's inline item toggles
 * already handle checklist data entry, so a separate tray would be
 * redundant (Skip 2026-05-24 — option (a)).
 *
 * Per-primitive input:
 *   - count:      number input (whole counts typical), unit suffix
 *   - percentage: number input clamped 0-100, "%" suffix
 *   - currency:   number input, currency-code suffix
 *
 * Optional note field for context. Save writes today's reporting
 * date via upsertMissionPoint (POST→409→PUT pattern, mirrors
 * MissionDataEntry on FP). Re-saving today overwrites today's row.
 *
 * Future expansion (not in 7.3a-ii): pick an arbitrary reporting
 * date (back-fill), show recent history below the input, etc.
 */

import React, { useCallback, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { MiniProjectGoalView } from "../../Data/MiniProjectConfig/miniProjectGoalView";
import { Tray } from "../Tray/Tray";
import { upsertMissionPoint, todayIso } from "./upsertMissionPoint";
import styles from "./GoalEditTray.module.css";

interface Props {
  /** Drives the slide-in/out of the shared Tray. */
  open: boolean;
  /** The goal the Lead is recording data for. */
  goal: MiniProjectGoalView | null;
  onClose: () => void;
  /** Fires after a successful write. Parent should refetch goal data. */
  onMutated: () => void;
}

function formatCurrencyHint(code: string | null | undefined): string {
  if (!code) return "USD";
  return code.toUpperCase();
}

const GoalDataEntryTray: React.FC<Props> = ({ open, goal, onClose, onMutated }) => {
  const { t } = useAppTranslation("pages");

  // Pre-fill with current value so the Lead can see what they're
  // overwriting. If they save without changing the number, the upsert
  // still writes a dated point for today (the controller will 409 /
  // PUT the existing row — idempotent from the Lead's POV).
  const [valueDraft, setValueDraft] = useState<string>(
    goal?.currentValue != null ? String(goal.currentValue) : "",
  );
  const [note, setNote] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    if (!goal) return;
    const n = Number(valueDraft);
    if (!Number.isFinite(n)) {
      setError(t("miniProject.cards.goal.invalidNumber"));
      return;
    }
    if (goal.primitive === "percentage" && (n < 0 || n > 100)) {
      setError(t("miniProject.cards.goal.dataEntry.percentageRange"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await upsertMissionPoint({
        miniProjectGuid: goal.miniProjectGuid,
        subKpiKey: goal.goalGuid,
        reportingDate: todayIso(),
        value: n,
        note: note.trim() || undefined,
      });
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [goal, valueDraft, note, onMutated, t]);

  // Per-primitive suffix shown next to the input.
  const inputSuffix = goal
    ? goal.primitive === "percentage"
      ? "%"
      : goal.primitive === "currency"
        ? formatCurrencyHint(goal.currencyCode)
        : goal.unitKey || ""
    : "";

  // Per-primitive label & hint copy.
  const inputLabel = goal
    ? t(`miniProject.cards.goal.dataEntry.label.${goal.primitive}`)
    : "";
  const inputHint = goal
    ? t(`miniProject.cards.goal.dataEntry.hint.${goal.primitive}`)
    : "";

  return (
    <Tray
      open={open}
      onClose={onClose}
      title={goal ? t("miniProject.cards.goal.dataEntry.trayTitle", { name: goal.nameKey }) : t("miniProject.cards.goal.dataEntry.title")}
    >
      {goal && (
        <>
          <Tray.Body className={styles.body}>
            <div className={styles.sectionLabel}>
              {t("miniProject.cards.goal.dataEntry.todayLabel")}
            </div>

            <div className={styles.row}>
              <label className={styles.label}>{inputLabel}</label>
              <div className={styles.deadlineBox}>
                <input
                  type="number"
                  step="any"
                  className={styles.input}
                  value={valueDraft}
                  onChange={(e) => setValueDraft(e.target.value)}
                  disabled={busy}
                  autoFocus
                />
                {inputSuffix && (
                  <span className={styles.checkHint}>{inputSuffix}</span>
                )}
              </div>
              {inputHint && <div className={styles.hint}>{inputHint}</div>}
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.cards.goal.dataEntry.noteLabel")}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                type="text"
                className={styles.input}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("miniProject.cards.goal.dataEntry.notePlaceholder")}
                disabled={busy}
              />
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}
          </Tray.Body>

          <Tray.Footer className={styles.footer}>
            <button
              type="button"
              className={styles.btn}
              onClick={onClose}
              disabled={busy}
              style={{ marginInlineStart: "auto" }}
            >
              {t("miniProject.sections.button.cancel")}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSave}
              disabled={busy}
            >
              {busy ? t("miniProject.sections.button.saving") : t("miniProject.cards.goal.dataEntry.saveButton")}
            </button>
          </Tray.Footer>
        </>
      )}
    </Tray>
  );
};

export default GoalDataEntryTray;
